import { type UserPersonalDataWithAreaOfInterestsDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useApiClient } from '@/api/util/useApiClient.util.ts';
import { useUserContext } from '@/utils/providers/userContext/useUserContext.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

type Response = UserPersonalDataWithAreaOfInterestsDto;

type Return = {
  error: Error | null;
  isGetting: boolean;
  userPersonalData: Response | undefined;
};

export const useGetUserPersonalData = (): Return => {
  const { restUserApi } = useApiClient();
  const { user } = useUserContext();
  const { t } = useTranslationWithPrefix('utils.errors');
  const { data, error, isFetching } = useQuery<Response>({
    enabled: !!user?.userId,
    queryFn: async () => {
      if (!user?.userId) {
        toast.error(t('unexpectedError'));
        throw new Error('Unexpected error - userId is not defined');
      }
      const { data } = await restUserApi.getUserPersonalData(user.userId);
      return data;
    },
    queryKey: [QueryKeyEnum.GET_USER_PERSONAL_DATA, user?.userId],
  });

  return {
    error,
    isGetting: isFetching,
    userPersonalData: data,
  };
};
